import { PaletteColors } from '@/Theme';
import { isMobileViewport } from '@/composables/useIsMobile';
import State from '@/state/State';
import { toCurrency } from '@/utils';
import { hexToRGB, toRGBParts } from '@/utils/color';

export const TIME_SERIES = {
  WEEK_TO_WEEK: 'w2w',
  MONTH_TO_MONTH: 'm2m',
};

export const TIME_SERIES_VALUES = Object.values(TIME_SERIES);

export const DEFAULT_DATE_FORMAT = 'DD MMM';

export const TIME_SERIES_DATE_FORMAT = {
  [TIME_SERIES.WEEK_TO_WEEK]: DEFAULT_DATE_FORMAT,
  [TIME_SERIES.MONTH_TO_MONTH]: 'MMM YY',
};

export const TIME_SERIES_UNIT = {
  [TIME_SERIES.WEEK_TO_WEEK]: 'week',
  [TIME_SERIES.MONTH_TO_MONTH]: 'month',
};

export const DEFAULT_TOOLTIP_STYLES = {
  boxPadding: 5,
};

export const getDefaultGridStyles = () => {
  const isMobile = isMobileViewport();
  return {
    grid: {
      borderColor: PaletteColors['default-lighten-80'],
      borderDash: [2, 2],
      color: PaletteColors['default-lighten-80'],
    },
    ticks: {
      color: PaletteColors['default-lighten-30'],
      padding: 5,
      maxRotation: isMobile ? 45 : 0,
      autoSkip: true,
      maxTicksLimit: isMobile ? 6 : 12,
      font: {
        size: isMobile ? 10 : 12,
      },
    },
    title: {
      color: PaletteColors['default-lighten-30'],
    },
  };
};

export const getDatasetColor = index => {
  const datasetColorList = Object.entries(PaletteColors)
    .filter(([key]) => (key.startsWith('brand') || key.startsWith('core')) && !key.includes('lighten'))
    .map(([value]) => value)
    .reverse();

  return datasetColorList[index % datasetColorList.length];
};

export const generateGradientBackground = (ctx, color) => {
  const canvas = ctx.chart.ctx;
  const height = canvas.canvas.height;
  const gradient = canvas.createLinearGradient(0, height / 8, 0, height);
  const [r, g, b] = toRGBParts(hexToRGB(color));

  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.5)`);
  gradient.addColorStop(0.25, `rgba(${r}, ${g}, ${b}, 0.3)`);
  gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

  return gradient;
};

export const getInitialAspectRatio = (defaultRatio, smRatio) => (isMobileViewport() ? smRatio : defaultRatio);

export const mobileNumberTickCallback = label => {
  if (isNaN(label)) {
    return label;
  }
  if (isMobileViewport() && Math.abs(label) >= 1000) {
    const absVal = Math.abs(label);
    const sign = label < 0 ? '-' : '';
    if (absVal >= 1000000) {
      const num = absVal / 1000000;
      const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
      return `${sign}${formatted}M`;
    }
    if (absVal >= 1000) {
      const num = absVal / 1000;
      const formatted = num % 1 === 0 ? num.toFixed(0) : num.toFixed(1);
      return `${sign}${formatted}k`;
    }
  }
  return label.toLocaleString();
};

const CURRENCY_CONFIG = {
  USD: { currency: 'USD', locale: 'en-US' },
  CAD: { currency: 'CAD', locale: 'en-CA' },
  EUR: { currency: 'EUR', locale: 'en-UK' },
  AUD: { currency: 'AUD', locale: 'en-AU' },
};
const DEFAULT_CURRENCY_CONFIG = { currency: 'GBP', locale: 'en-UK' };

const getCompactCurrency = value => {
  const code = State.state?.company?.default_currency;
  const { currency, locale } = CURRENCY_CONFIG[code] || DEFAULT_CURRENCY_CONFIG;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value / 100);
};

export const mobileCurrencyTickCallback = label => {
  if (isNaN(label)) {
    return label;
  }
  const value = label * 100;
  if (isMobileViewport() && Math.abs(value) >= 100000) {
    return getCompactCurrency(value);
  }
  return toCurrency(value);
};

export const shouldShowDataLabel = context => {
  const data = context.dataset.data;
  const i = context.dataIndex;
  if (data[i] == null) {
    return false;
  }
  if (data.length <= 8) {
    return true;
  }
  if (i === 0 || i === data.length - 1) {
    return true;
  }
  const prev = data[i - 1];
  const curr = data[i];
  const next = data[i + 1];
  if (curr == null || prev == null || next == null) {
    return curr != null;
  }
  const isPeak = curr > prev && curr >= next;
  const isTrough = curr < prev && curr <= next;
  return isPeak || isTrough;
};

export const createCountWithPercentageTooltip = percentageDataset => ({
  plugins: {
    tooltip: {
      callbacks: {
        label: context => {
          const count = context.parsed.y;
          if (count == null) {
            return null;
          }
          const formatted = count.toLocaleString('en-GB');
          const percentage = percentageDataset?.values?.[context.dataIndex];
          return percentage != null
            ? `${context.dataset.label}: ${formatted} · ${percentage}%`
            : `${context.dataset.label}: ${formatted}`;
        },
      },
    },
  },
});
